/* =========================================
   UNTOLD STORY JEWELLERY — style quiz
   Scores the product cards the section already rendered and reveals the best
   matches. It never builds a product card itself: prices, images and the
   add-to-cart forms all stay Liquid, so money is formatted by Shopify.

   Spec: untoldstories/seo/quiz-design-document.md §3
   ========================================= */

(function () {
  'use strict';

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* Q3 carries the strongest signal, so a primary style match counts twice. */
  const WEIGHT = { who: 3, occasion: 3, style: 4, budget: 2 };
  const ORDER = ['who', 'occasion', 'style', 'budget'];

  function init(root) {
    if (!root || root.dataset.quizReady === 'true') return;
    root.dataset.quizReady = 'true';

    const questionData = readJSON($('[data-quiz-questions]', root));
    const strings = readJSON($('[data-quiz-strings]', root));
    if (!questionData || !strings) return;

    const questions = ORDER
      .filter((key) => questionData[key])
      .map((key) => Object.assign({ key: key }, questionData[key]));
    if (!questions.length) return;

    const stage = $('[data-quiz-stage]', root);
    const bar = $('[data-quiz-bar]', root);
    const live = $('[data-quiz-live]', root);
    const pool = $('[data-quiz-pool]', root);
    const captureTpl = $('[data-quiz-capture-template]', root);
    const resultsCount = parseInt(root.dataset.resultsCount, 10) || 3;

    const candidates = $$('[data-quiz-candidate]', pool).map((el) => ({
      el: el,
      price: parseInt(el.dataset.price, 10) || 0,
      type: el.dataset.type || '',
      style: tokens(el.dataset.style),
      who: tokens(el.dataset.who),
      occasion: tokens(el.dataset.occasion)
    }));

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const storageKey = 'us_quiz_' + (root.id || 'default');

    let step = 0;
    let answers = {};

    restore();
    render();

    /* ---------- state ---------- */

    function save() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ step: step, answers: answers }));
      } catch (e) {
        /* private browsing: the quiz still works, it just will not resume */
      }
    }

    function restore() {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (saved && saved.answers) {
          answers = saved.answers;
          step = Math.min(saved.step || 0, questions.length);
        }
      } catch (e) {
        /* ignore malformed state */
      }
    }

    /* ---------- render ---------- */

    function render() {
      if (step >= questions.length) return renderResults();
      renderQuestion(questions[step]);
    }

    function renderQuestion(q) {
      const progress = (step / questions.length) * 100;
      bar.style.width = progress + '%';
      bar.parentElement.setAttribute('aria-valuenow', String(step));

      const counter = fill(strings.stepOf, {
        current: pad(step + 1),
        total: pad(questions.length)
      });

      const frag = document.createElement('div');
      frag.className = 'style-quiz__step';

      if (step > 0) {
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'style-quiz__back';
        back.textContent = strings.back;
        back.addEventListener('click', goBack);
        frag.appendChild(back);
      }

      const head = document.createElement('header');
      head.className = 'style-quiz__head';
      head.appendChild(make('span', 'style-quiz__counter', counter, { 'aria-hidden': 'true' }));
      const h = make('h2', 'style-quiz__headline', q.headline);
      h.tabIndex = -1;
      head.appendChild(h);
      head.appendChild(make('p', 'style-quiz__sub', q.sub));
      frag.appendChild(head);

      /* An image question with no images configured degrades to typographic
         cards rather than rendering four broken boxes. */
      let type = q.type;
      if (type === 'image' && !q.options.some((o) => o.img)) type = 'choice';

      const grid = document.createElement('div');
      grid.className = 'style-quiz__grid style-quiz__grid--' + type;
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', q.headline);

      q.options.forEach((opt, i) => {
        const selected = answers[q.key] === opt.value;
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'style-quiz__card' + (opt.muted ? ' style-quiz__card--muted' : '') +
          (selected ? ' is-selected' : '');
        card.setAttribute('role', 'radio');
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
        card.tabIndex = selected || (!answers[q.key] && i === 0) ? 0 : -1;
        card.dataset.value = opt.value;

        if (type === 'image') {
          const media = make('span', 'style-quiz__media');
          const img = document.createElement('img');
          img.src = opt.img;
          img.alt = '';
          img.loading = i < 3 ? 'eager' : 'lazy';
          img.width = 700;
          img.height = 875;
          media.appendChild(img);
          card.appendChild(media);
        } else if (type === 'choice') {
          card.appendChild(make('span', 'style-quiz__rule', '', { 'aria-hidden': 'true' }));
        }

        card.appendChild(make('span', 'style-quiz__label', opt.label));
        if (type === 'choice' && opt.note) {
          card.appendChild(make('span', 'style-quiz__note', opt.note));
        }

        card.addEventListener('click', () => choose(q, opt.value));
        grid.appendChild(card);
      });

      wireRovingFocus(grid);
      frag.appendChild(grid);

      stage.replaceChildren(frag);
      announce(fill(strings.liveStep, {
        current: String(step + 1),
        total: String(questions.length),
        question: q.headline
      }));
      focusHeading();
    }

    function choose(q, value) {
      answers[q.key] = value;

      $$('.style-quiz__card', stage).forEach((c) => {
        const on = c.dataset.value === value;
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      const advance = () => {
        step += 1;
        save();
        render();
      };
      if (reduced) advance();
      else window.setTimeout(advance, 260);
    }

    function goBack() {
      if (step === 0) return;
      step -= 1;
      save();
      render();
    }

    /* ---------- results ---------- */

    function renderResults() {
      bar.style.width = '100%';

      const picks = pickResults();

      const frag = document.createElement('div');
      frag.className = 'style-quiz__results';

      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'style-quiz__back';
      back.textContent = strings.back;
      back.addEventListener('click', goBack);
      frag.appendChild(back);

      const head = document.createElement('header');
      head.className = 'style-quiz__head';
      head.appendChild(make('span', 'style-quiz__counter', strings.resultsEyebrow));
      const h = make('h2', 'style-quiz__headline',
        fill(strings.resultsHeading, { count: String(picks.length) }));
      h.tabIndex = -1;
      head.appendChild(h);
      head.appendChild(make('p', 'style-quiz__sub', strings.resultsSub));
      frag.appendChild(head);

      const grid = document.createElement('div');
      grid.className = 'style-quiz__results-grid';
      picks.forEach((c, i) => {
        /* Move the Liquid-rendered card itself, so its add-to-cart form,
           money formatting and image srcset all survive intact. */
        const card = c.el;
        card.hidden = false;
        if (!$('.style-quiz__why', card)) {
          card.appendChild(make('p', 'style-quiz__why', whyLine(i)));
        }
        grid.appendChild(card);
      });
      frag.appendChild(grid);

      if (strings.craft) frag.appendChild(make('p', 'style-quiz__craft', strings.craft));

      if (captureTpl) {
        const capture = captureTpl.content.cloneNode(true);
        const tags = $('[data-quiz-tags]', capture);
        if (tags) tags.value = tagString();
        frag.appendChild(capture);
      }

      const actions = document.createElement('div');
      actions.className = 'style-quiz__actions';
      const restart = document.createElement('button');
      restart.type = 'button';
      restart.className = 'style-quiz__restart';
      restart.textContent = strings.restart;
      restart.addEventListener('click', () => {
        answers = {};
        step = 0;
        save();
        resetPool();
        render();
      });
      actions.appendChild(restart);

      if (strings.browseUrl) {
        const browse = document.createElement('a');
        browse.className = 'style-quiz__browse';
        browse.href = strings.browseUrl;
        browse.textContent = strings.browse;
        actions.appendChild(browse);
      }
      frag.appendChild(actions);

      stage.replaceChildren(frag);
      announce(fill(strings.liveResults, { count: String(picks.length) }));
      focusHeading();
    }

    function resetPool() {
      candidates.forEach((c) => {
        const why = $('.style-quiz__why', c.el);
        if (why) why.remove();
        c.el.hidden = true;
        pool.appendChild(c.el);
      });
    }

    /* ---------- scoring ---------- */

    function score(c) {
      let s = 0;
      if (answers.who && c.who.indexOf(answers.who) !== -1) s += WEIGHT.who;
      if (answers.occasion && c.occasion.indexOf(answers.occasion) !== -1) s += WEIGHT.occasion;
      if (answers.style && c.style.indexOf(answers.style) !== -1) s += WEIGHT.style;
      if (answers.style && c.style[0] === answers.style) s += WEIGHT.style;

      /* Budget narrows rather than excludes: an out-of-band piece can still
         win if it is the best match on everything that matters more. */
      if (answers.budget && answers.budget !== 'skip') {
        const band = bandFor(answers.budget);
        if (band) {
          const inBand = (band.min === undefined || c.price >= band.min) &&
            (band.max === undefined || c.price < band.max);
          s += inBand ? WEIGHT.budget : -WEIGHT.budget;
        }
      }
      return s;
    }

    function bandFor(value) {
      const q = questions.filter((x) => x.key === 'budget')[0];
      if (!q) return null;
      return q.options.filter((o) => o.value === value)[0] || null;
    }

    function pickResults() {
      const ranked = candidates
        .map((c) => ({ c: c, s: score(c) }))
        .sort((a, b) => b.s - a.s || a.c.price - b.c.price);

      /* Spread across product types so the three picks do not all look alike. */
      const picks = [];
      const seen = {};
      ranked.forEach((r) => {
        if (picks.length >= resultsCount) return;
        if (r.c.type && seen[r.c.type]) return;
        picks.push(r.c);
        seen[r.c.type] = true;
      });
      ranked.forEach((r) => {
        if (picks.length >= resultsCount) return;
        if (picks.indexOf(r.c) === -1) picks.push(r.c);
      });
      return picks;
    }

    /* ---------- copy ---------- */

    function phraseFor(key) {
      const q = questions.filter((x) => x.key === key)[0];
      if (!q || !answers[key]) return '';
      const opt = q.options.filter((o) => o.value === answers[key])[0];
      if (!opt) return '';
      return opt.phrase !== undefined ? opt.phrase : opt.label;
    }

    function whyLine(index) {
      const who = phraseFor('who');
      const style = phraseFor('style');
      const occasion = phraseFor('occasion');

      if (index === 1 && occasion) return fill(strings.whyOccasion, { occasion: occasion });
      if (index === 2) return fill(strings.whyThird, { style: style, who: who });
      return fill(strings.whyPrimary, { who: who, style: style });
    }

    /* Tags land on the customer record and are what Shopify Email and Klaviyo
       segment on. Prefixed so they never collide with merchandising tags. */
    function tagString() {
      const tags = ['quiz'];
      ORDER.forEach((key) => {
        if (answers[key] && answers[key] !== 'skip') {
          tags.push('quiz-' + key + '-' + answers[key]);
        }
      });
      return tags.join(', ');
    }

    /* ---------- helpers ---------- */

    function wireRovingFocus(grid) {
      const cards = $$('.style-quiz__card', grid);
      cards.forEach((card, i) => {
        card.addEventListener('keydown', (e) => {
          const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
          if (!delta) return;
          e.preventDefault();
          const next = cards[(i + delta + cards.length) % cards.length];
          cards.forEach((c) => { c.tabIndex = -1; });
          next.tabIndex = 0;
          next.focus();
        });
      });
    }

    function focusHeading() {
      const h = $('.style-quiz__headline', stage);
      if (h) h.focus({ preventScroll: true });
      const top = root.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > top) window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
    }

    function announce(text) {
      if (live) live.textContent = text;
    }
  }

  function tokens(value) {
    return (value || '').split(/[\s,]+/).filter(Boolean);
  }

  /* Shopify's `t` filter HTML-escapes what it returns, which is right when the
     value lands in markup and wrong when it lands in JSON that this script
     then writes with textContent. A locale string of "Classic & timeless"
     arrives here as "Classic &amp; timeless" and was rendered on the tile
     exactly like that. Decoded once on the way in rather than at each of the
     nine places a string is written, so a translator can use &, < or an
     apostrophe without knowing any of this.

     A detached <textarea> parses its content as text, never as markup, so
     nothing in a locale file can create an element or run through this. */
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    const holder = document.createElement('textarea');
    holder.innerHTML = value;
    return holder.value;
  }

  function decodeDeep(value) {
    if (typeof value === 'string') return decodeEntities(value);
    if (Array.isArray(value)) return value.map(decodeDeep);
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((k) => { value[k] = decodeDeep(value[k]); });
    }
    return value;
  }

  function readJSON(el) {
    if (!el) return null;
    try {
      return decodeDeep(JSON.parse(el.textContent));
    } catch (e) {
      return null;
    }
  }

  function make(tag, className, text, attrs) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    if (attrs) Object.keys(attrs).forEach((k) => el.setAttribute(k, attrs[k]));
    return el;
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  /* Locale strings carry [placeholders] so translators keep control of order. */
  function fill(template, values) {
    if (!template) return '';
    return Object.keys(values).reduce(
      (out, key) => out.split('[' + key + ']').join(values[key]),
      template
    );
  }

  function boot(scope) {
    $$('[data-style-quiz]', scope || document).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  /* Theme editor: re-init when the section is re-rendered. */
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
